// Forms — shared safe Zod resolver (use instead of @hookform/resolvers/zod)
export { safeZodResolver, zodResolver } from "./forms/safe-zod-resolver";
export { scrollToFirstError } from "./forms/scroll-to-first-error";

// Components
export { Button } from "./components/button";
export { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/card";
export { SimpleVideoPlayer } from "./components/simple-video-player";
export { Testimonials } from "./components/testimonials";
export type { Testimonial, VideoTestimonial } from "./components/testimonials";
export { PartnerSlackCTA } from "./components/PartnerSlackCTA";
export type { PartnerSlackCTAProps } from "./components/PartnerSlackCTA";

// Booking Components
export * from "./components/booking";
export { PublicBookingStepper } from "./components/unifiedtime/PublicBookingStepper";
export { BookingLinkBuilderPanel } from "./components/unifiedtime/BookingLinkBuilderPanel";

// Booking Hooks
export * from "./hooks/booking";

// Shared Components
export { TimeSlotGrid } from "./components/shared/TimeSlotGrid";
export { AdvancedBookingForm } from "./components/shared/AdvancedBookingForm";
export { default as BookingModal } from "./components/shared/BookingModal";

// UI Components
export { Tabs, TabsList, TabsTrigger, TabsContent } from "./components/tabs";
export { EnhancedTabs, EnhancedTabsList, EnhancedTabsTrigger, EnhancedTabsContent } from "./components/enhanced-tabs";
export { Badge, badgeVariants } from "./components/badge";

// Safe Components (Context-independent alternatives)
export { SafeCloseButton, useSafeClose, withSafeClose } from "./components/safe-close-button";
export { Alert, AlertTitle, AlertDescription } from "./components/alert";

// Mantine Integration
export { MantineProvider } from "./components/mantine-provider";
export { MantineModalStack, useUrlModalStack } from "./components/mantine-modal-stack";
export { MantineDrawerStack, useUrlDrawerStack } from "./components/mantine-drawer-stack";
export { SimpleModal } from "./components/simple-modal";
export { SimpleDrawer } from "./components/simple-drawer";
export { GlobalModalStackProvider, useGlobalModalStack, GlobalModalStackContext } from "./components/global-modal-stack";
export { MODAL_IDS, GLOBAL_MODAL_IDS } from "./components/global-modal-stack/constants";
export type { ModalId } from "./components/global-modal-stack/constants";
export { ModalContainer } from "./components/global-modal-stack/ModalContainer";
export type { ModalContainerProps } from "./components/global-modal-stack/ModalContainer";
export { GlobalDrawerStackProvider, useGlobalDrawerStack, GlobalDrawerStackContext, CentralizedDrawer } from "./components/global-drawer-stack";
export { useStackModal } from "./hooks/use-stack-modal";
export { useStackDrawer } from "./hooks/use-stack-drawer";
export { MantineSelect, MantineMultiSelect } from "./components/mantine-select";
export { 
  Dialog, 
  DialogPortal, 
  DialogOverlay, 
  DialogClose, 
  DialogTrigger, 
  DialogContent, 
  DialogHeader, 
  DialogFooter, 
  DialogTitle, 
  DialogDescription 
} from "./components/dialog";
export { ConfirmationModal } from "./components/confirmation-modal";
export { ResponsiveModal, useResponsiveModal } from "./components/responsive-modal";
export type { ResponsiveModalProps } from "./components/responsive-modal";
// export { MantineModalStack, useUrlModalStack } from "./components/mantine-modal-stack";

// Drawer Stack System
export { 
  DrawerStackProvider, 
  useDrawerStack, 
  useDrawerStackRequired,
  useDrawerLevel, 
  useDrawerZIndex, 
  useCanNest, 
  useShouldReplace 
} from "./lib/contexts/drawer-stack-context";
export { 
  drawerRouter, 
  createDrawerContext, 
  analyzeContentComplexity, 
  detectContentType 
} from "./lib/drawer-router";
export type { 
  DrawerItem, 
  DrawerConfig, 
  DrawerStackContextType,
  DrawerContext,
  DrawerRoutingRules
} from "./lib/contexts/drawer-stack-context";
export { Input } from "./components/input";
export { Label } from "./components/label";
export { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "./components/select";
export { Textarea } from "./components/textarea";
export { LoadingSpinner } from "./components/loading-spinner";
export { Skeleton } from "./components/skeleton";
export { DiagnosticButton } from "./components/DiagnosticButton";
export { Accordion, AccordionContent, AccordionItem, AccordionItemWithContext, AccordionTrigger } from "./components/accordion";
export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./components/tooltip";
export { HoverTip } from "./components/hover-tip";
export type { HoverTipProps } from "./components/hover-tip";
export { Separator } from "./components/separator";
export { ScrollArea, ScrollBar } from "./components/scroll-area";

// Table Components
export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
} from "./components/table";

// Drawer Components
export {
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
} from "./components/drawer";

// Form Components (react-hook-form integration)
export {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
  useFormField,
} from "./components/form";

// Checkbox Component
export { Checkbox } from "./components/checkbox";

// Switch Component
export { Switch } from "./components/switch";

// Calendar Component
export { Calendar } from "./components/calendar";

// Markdown Components
export { MarkdownRenderer } from "./components/markdown/markdown-renderer";

// Hooks
export { useCopyToClipboard } from "./hooks/useCopyToClipboard";
export { useIsMobile } from "./hooks/useIsMobile";
export { useGoogleOAuth } from "./hooks/use-google-oauth";

// Search Components
export { AdvancedFullPageSearch } from "./components/AdvancedFullPageSearch";
export { SearchFilters } from "./components/SearchFilters";
export { SearchResultGroup } from "./components/SearchResultGroup";
export { SearchResultGroupCollapsible } from "./components/SearchResultGroupCollapsible";
export { SearchResultGroupSidebar } from "./components/SearchResultGroupSidebar";
export { SearchSuggestions } from "./components/SearchSuggestions";
export { SearchLoadingSkeleton } from "./components/SearchLoadingSkeleton";
export { SearchHistoryDrawer } from "./components/SearchHistoryDrawer";
export { SearchModal } from "./components/SearchModal";
export { SearchFilterChips } from "./components/SearchFilterChips";
export { SearchFilterPopover } from "./components/SearchFilterPopover";
export type { SearchResult, UseAlgoliaSearchReturn } from "./components/AdvancedFullPageSearch";

// Search Hooks
export { useSearchHistory } from "./hooks/useSearchHistory";
export type { SearchHistoryItem } from "./hooks/useSearchHistory";
export { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "./components/alert-dialog";
export { Popover, PopoverContent, PopoverTrigger } from "./components/popover";
export { 
  DropdownMenu, 
  DropdownMenuTrigger, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuCheckboxItem, 
  DropdownMenuRadioGroup, 
  DropdownMenuRadioItem, 
  DropdownMenuSeparator, 
  DropdownMenuShortcut, 
  DropdownMenuSub, 
  DropdownMenuSubTrigger, 
  DropdownMenuSubContent 
} from "./components/dropdown-menu";
export { RadioGroup, RadioGroupItem } from "./components/radio-group";
export { Toaster, showToast } from "./components/toast";

// Responsive Select Components
export { ResponsiveCommandSelect, ResponsivePopover } from "./components/responsive-select";
export type { SelectOption, BaseSelectProps, RecentItem } from "./components/responsive-select";

// OAuth Components
export { GoogleOAuthSetup } from "./components/unifiedtime/google/GoogleOAuthSetup";
export { MicrosoftOAuthSetup } from "./components/unifiedtime/microsoft/MicrosoftOAuthSetup";
export { default as OAuthIntermediatePage } from "./components/oauth/OAuthIntermediatePage";
export { GoogleSignInButton } from "./components/oauth/GoogleSignInButton";

// Shared Auth Components
export { SharedAuthPage, SharedAuthProvider, useSharedAuth, getAuthConfig, defaultAuthConfig, appConfigs } from "./components/auth";
export type { AuthConfig } from "./components/auth";

// Setup Page
export { default as SetupPage } from "./components/unifiedtime/setup-page";

// Calendar Pages
export { default as CalendarHomePage } from "./components/unifiedtime/calendar-home-page";
export { default as BookPage } from "./components/unifiedtime/book-page";
export { default as GooglePage } from "./components/unifiedtime/google-page";
export { default as MicrosoftPage } from "./components/unifiedtime/microsoft-page";
export { default as UnifiedPage } from "./components/unifiedtime/unified-page";
export { default as PublicBookingPage } from "./components/unifiedtime/public-booking-page";

// Calendar Components
export { AdvancedCalendar } from "./components/unifiedtime/AdvancedCalendar";
export { EventDetailsModal } from "./components/unifiedtime/EventDetailsModal";
export { EventEditDialog } from "./components/unifiedtime/EventEditDialog";
export { EventAttendeesTab } from "./components/unifiedtime/EventAttendeesTab";
export { AttendeeDetailsModal } from "./components/unifiedtime/AttendeeDetailsModal";
export { PersonSelector } from "./components/unifiedtime/PersonSelector";
export { CalendarMultiSelector } from "./components/unifiedtime/CalendarMultiSelector";
export { CreateMeetingModal } from "./components/unifiedtime/CreateMeetingModal";

// Attendee Components
export { AttendeeAvatar } from "./components/unifiedtime/attendee/AttendeeAvatar";
export { AttendeeStatusBadge } from "./components/unifiedtime/attendee/AttendeeStatusBadge";
export { AttendeeItem } from "./components/unifiedtime/attendee/AttendeeItem";
export { AttendeeList } from "./components/unifiedtime/attendee/AttendeeList";
export { default as EnhancedAttendeeFields } from "./components/unifiedtime/attendees/EnhancedAttendeeFields";
export { AttendeeChip } from "./components/unifiedtime/attendees/AttendeeChip";
export { AttendeeChips } from "./components/unifiedtime/attendees/AttendeeChips";
export { AttendeeSelectWithModal } from "./components/unifiedtime/attendees/AttendeeSelectWithModal";
export { default as CreateEventModal } from "./components/unifiedtime/calendarinvites/microsoft/CreateEventModal";

// Shared UI Components
export { SectionHeader } from "./components/unifiedtime/shared/SectionHeader";
export { EmptyState } from "./components/unifiedtime/shared/EmptyState";
export { LocalizedLink } from "./components/shared/LocalizedLink";
export { ActionButtonGroup } from "./components/unifiedtime/shared/ActionButtonGroup";
export { StandardModal } from "./components/unifiedtime/shared/StandardModal";
export { SummaryStats } from "./components/unifiedtime/shared/SummaryStats";

// Hooks
export { useToast } from "./hooks/use-toast";
export { useLocalDrawerSearch, useAsyncDrawerSearch, useDrawerSearch } from "./hooks/use-drawer-search";
export { useAttendeeData } from "./hooks/useAttendeeData";
export { useAuth } from "./hooks/useAuth";
export { useUrlModalControls } from "./hooks/use-url-modal-controls";
export { useUrlModal } from "./hooks/use-url-modal";
export {
  useHistoryModal,
  type UseHistoryModalOptions,
  type UseHistoryModalReturn,
} from "./hooks/use-history-modal";
export { 
  useAccountStatus, 
  useRemoveConnectionMutation, 
  useGoogleCalendarData, 
  useMicrosoftCalendarData, 
  useCombinedCalendarData, 
  useOpenSlots, 
  useBookingMutation, 
  useResetAccountMutation, 
  useGoogleOAuthSetup, 
  useMicrosoftOAuthSetup, 
  useCRMSearch,
  useExternalGoogleEvents,
  useExternalMicrosoftEvents,
  useCreateGoogleEvent,
  useCreateMicrosoftEvent,
  useDeleteGoogleEvent,
  useDeleteMicrosoftEvent,
  useDeleteEvent,
  useUpdateGoogleEvent,
  useUpdateMicrosoftEvent,
  useUpdateEvent
} from "./hooks/use-api";
export { useAccountRequired } from "./hooks/useAccountRequired";
export { useBookingMutation as useBookingMutationFromFile } from "./hooks/useBookingMutation";

// Utils
export { cn } from "./lib/utils";
export { generateGoogleAuthUrl, generateMicrosoftAuthUrl } from "./lib/oauth-utils";
export { apiGet, apiPost, apiDelete, apiPut } from "./lib/api-utils";
export { joinPartnerEcosystem } from "./lib/partner-ecosystem";
export type { JoinPartnerEcosystemOptions, JoinPartnerEcosystemResponse } from "./lib/partner-ecosystem";

// Attendee Utils
export {
  normalizeGoogleAttendees,
  normalizeMicrosoftAttendees,
  normalizeGoogleOrganizer,
  normalizeMicrosoftOrganizer,
  normalizeStringAttendees,
  normalizeStringOrganizer,
  extractAttendeeEmails,
  groupAttendeesByStatus,
  type AttendeeData
} from "./lib/attendee-utils";

// Auth Utils — NOT re-exported from the barrel because `./lib/auth-utils`
// transitively imports `next/server`, which is forbidden in the Chrome
// extension bundle. Import directly from `@wtc/ui/lib/auth-utils` in
// server-only code (Hub API routes, unifiedtime API routes).

// Universal Auth Check (Chrome redirect fix)
export {
  hasAuthCookies,
  getCachedAuthState,
  setCachedAuthState,
  clearCachedAuthState,
  isLikelyAuthenticated,
  checkAuthWithServer,
  checkAuthentication,
  useUniversalAuthCheck,
  detectAuthToken,
  debugAuthSources
} from "./lib/auth-check";
export type { AuthTokenInfo } from "./lib/auth-check";

// Optimal Auth System (Best Practices Implementation)
export {
  OptimalAuthProvider,
  useOptimalAuth,
  UniversalAuthProvider, // Drop-in replacement
  useUniversalAuth,      // Drop-in replacement
  useUniversalAuthOptional, // Non-throwing variant for not-found / error boundaries
  SimpleAuthProvider,
  useSimpleAuth,
  useSimpleAuthOptional,
  UserDataProvider,
  useUserData,
  useUserDataOptional
} from "./lib/auth/OptimalAuthProvider";

// Supabase Utils
export {
  getGoogleTokensFromSupabase,
  saveGoogleTokensToSupabase,
  getMsftTokensFromSupabase,
  saveMsftTokensToSupabase,
  deleteGoogleTokensFromSupabase,
  deleteMsftTokensFromSupabase,
  logAuditEvent
} from "./lib/supabase";

// Debug Logger
export { DebugLogger, startTimer } from "./lib/debug-logger";

// Calendar Service
export {
  CalendarService,
  DEFAULT_CONFIG,
  CalendarAvailabilityError,
  CALENDAR_DEFAULT_TIMEZONE,
  normalizeSlotConfig,
  holidayKeysForYear,
} from "./lib/calendar-service";
export type {
  TimeSlot as CalendarTimeSlot,
  BusyEventLike as CalendarBusyEventLike,
  BusyInterval as CalendarBusyInterval,
  CalendarEventLike,
  CalendarEventsResult,
  RawEventTime as CalendarRawEventTime,
  SlotConfig as CalendarSlotConfig,
  SlotConfigInput as CalendarSlotConfigInput,
} from "./lib/calendar-service";

// Calendar Creation Service
export {
  createCalendarEvent,
  getCalendarCreationHeaders,
  createErrorResponse as createCalendarErrorResponse,
  validateCalendarCreationRequest,
  getCalendarTokens,
  transformToGoogleEventFormat,
  transformToMicrosoftEventFormat,
  createGoogleCalendarEvent,
  createMicrosoftCalendarEvent,
  type CalendarEventRequest,
  type CalendarEventResponse,
  type CalendarCreationResult,
  type CalendarProvider
} from "./lib/calendar-creation-service";

// API Shared Utils
export {
  getGoogleUserEmail,
  getMicrosoftUserEmail,
  getGoogleCredentials,
  getMicrosoftCredentials,
  // USER_OAUTH_TOKEN_REFRESH: `refreshGoogleAccessToken` was removed here in
  // Phase 4.5 wave 5, once its last consumer (an apps/unifiedtime calendar route)
  // adopted the shared seam over the ONE `@wtc/oauth-connections` engine. Its
  // Microsoft twin and the `refreshAccessToken` / `getUserEmail*` wrappers went in
  // wave 4. Do not re-export a refresh from this browser-reachable barrel.
  fetchGoogleCalendarEvents,
  fetchMicrosoftCalendarEvents,
  fetchExternalGoogleCalendarEvents,
  fetchExternalMicrosoftCalendarEvents,
  fetchCalendarEvents,
  fetchExternalCalendarEvents,
  isValidEmail,
  sanitizeInput,
  createErrorResponse,
  createSuccessResponse,
  handleCorsPreflight,
  createCorsHeaders,
  createMethodNotAllowedResponse,
  createAuthRequiredResponse,
  createMissingParamsResponse,
  type AccountStatus
} from "./lib/api-shared-utils";

// API Route Handlers
export {
  handleGetAccountStatus,
  handleGetOpenSlots,
  handlePublicBooking,
  handleGetPublicAvailability,
  handleClearAccount,
  handleRemoveConnection,
  handleExternalEventBooking,
  handleGetGoogleCalendarEvents,
  handleGetMicrosoftCalendarEvents,
  createRouteContext,
  createPublicRouteContext,
  handleRouteError,
  type RouteContext,
  type OpenSlotsParams,
  type PublicBookingParams
} from "./lib/api-route-handlers";

// Booking Configuration
export {
  getEventTypeConfig,
  getAllEventTypes,
  validateEventTypeConfig,
  type EventTypeConfig
} from "./lib/booking/event-types-config";

// Booking Round Robin
export {
  getNextAssignee,
  updateRoundRobinState
} from "./lib/booking/round-robin-assignment";

// Date utilities
export {
  parseDateString,
  formatDateString,
  isWeekend,
  isWeekday,
  getDayName,
  addDays,
  startOfDay,
  endOfDay,
  isSameDay,
  createDateRange,
  parseAndValidateDate
} from "./lib/date-utils";

// Route Utilities
export {
  createRouteHandler,
  createGetRoute,
  createPostRoute,
  createPutRoute,
  createDeleteRoute,
  createMethodNotAllowedHandlers,
  handleCorsPreflightRequest,
  parseRequestBody,
  validateRequestData,
  sanitizeInputs,
  CommonValidationRules,
  createSuccessResponseWithData,
  createStandardErrorResponse,
  createValidationErrorResponse,
  type RouteConfig,
  type ValidationRule,
  type RouteHandlerOptions
} from "./lib/route-utilities";

// CSV Export
export { downloadCSV, formatArrayForCSV, formatBooleanForCSV, formatCurrencyForCSV, formatDateForCSV, formatNumberForCSV, objectsToCSV, sanitizeFieldName } from "./lib/csv-export";

// Currency / BigFloat display (canonical — BigFloat outputs are strings)
export { formatCurrency, formatNumber, formatWholeCurrency, parseBigFloat } from "./lib/currency";
export type { BigFloatValue, FormatCurrencyOptions, FormatNumberOptions } from "./lib/currency";

// User Types and Constants
export * from "./lib/constants/user-types";

// Supabase Client and Auth Guard
export { createClient } from "./lib/supabase/client";
export { setGlobalAuthState, allowAuthContextCall, resetAuthContextCallTracking } from "./lib/supabase/global-auth-guard";

// Sign Out Utilities
export {
  signOut,
  signOutEverywhere,
  signOutOtherDevices,
} from "./lib/utils/sign-out";
export { useSignOut } from "./hooks/use-sign-out";
export type { SignOutOptions, SignOutResult } from "./lib/utils/sign-out";

// Supabase Auth Functions
export {
  signInWithSupabase,
  signUpWithSupabase,
  signOutWithSupabase,
  getCurrentSupabaseUser,
  createSupabaseUser,
  updateSupabaseUserMetadata,
  deleteSupabaseUser
} from "./lib/supabase-auth";

// Supabase Auth Context

// Legacy Universal Auth Context (deprecated - use OptimalAuthProvider instead)
// export { UniversalAuthProvider, useUniversalAuth } from "./lib/universal-auth-context";

// Route Components (still needed by unifiedtime)
export { SupabaseProtectedRoute, SupabasePublicRoute } from "./lib/contexts/supabase-auth-context";

// Shared Supabase browser client
export { getBrowserSupabaseClient } from "./lib/supabase-browser";

// HTTP client and authed query helper
export { apiClient, setAuthTokenGetter } from "./lib/http/client";
export { useAuthedQuery } from "./lib/query/useAuthedQuery";

// Preferences Context
export { PreferencesProvider, usePreferences } from "./lib/preferences-context";

// Calendar Context
export { 
  CalendarProvider as CalendarContextProvider, 
  useCalendarContext,
  useCalendarEvents,
  useCalendarEvent,
  useCalendarSources,
  useSourceColors,
  useSourceColor,
  type CalendarViewType
} from "./lib/calendar-context";

// FullCalendar shared CSS
// Note: CSS files are imported by path; no TS type is needed

// Affiliate Tracking
export { trackFunnelConversion, getAffiliateSessionId, clearAffiliateSession } from "./lib/affiliate-tracking";

// Hub unified calendar agenda (fetch-based; safe for non-Next contexts)
export {
  fetchHubUnifiedCalendarAgenda,
  type FetchHubUnifiedCalendarAgendaOptions,
  type HubUnifiedCalendarAgendaResult,
} from "./lib/hub-agenda";
export { WTC_EMAIL_CALENDAR_SOURCE_ID, formatWtcEmailCalendarSourceForDisplay } from "./hooks/api/calendar/constants";

// Types
export * from "./types/booking";
export * from "./types/calendar";
export * from "./lib/unified-calendar-shared-team-calendars";
export * from "./lib/types/user";
export * from "./lib/types/auth";
export * from "./lib/constants/testimonials";

// Organization Components
export { OrganizationSwitcher } from "./components/organization/OrganizationSwitcher";
export { TeamMembersList } from "./components/organization/TeamMembersList";
export { InviteMemberModal } from "./components/organization/InviteMemberModal";

// Organization Provider
export { OrganizationProvider, useOrganization } from "./lib/organization/OrganizationProvider";


// Charts — one dependency-free SVG chart engine shared by the website blog
// (```chart MDX blocks) and Meridian `chart` artifacts. See
// ./components/charts/ChartRenderer.
export { ChartSpecSchema, renderChart, type ChartSpec } from "./components/charts/ChartRenderer";
